import CustomChip from '@app/components/atoms/CustomChip/CustomChip'
import Section from '@app/components/atoms/Section/Section'
import CardWidget from '@app/components/CardWidget'
import ImageSlider from '@app/components/ImageSlider'
import MenuItem from '@app/components/MenuItem'
import ModalUpdateStatusCampaign from '@app/components/ModalUpdateStatusCampaign'
import OptionMenu from '@app/components/OptionMenu'
import { VOUCHER_STATUS } from '@app/constant/VoucherConstant'
import { VOUCHER_TYPE } from '@app/constant/VoucherTypeConstant'
import { formatNumber, formatPrice, toDateTime, toRemainingTime } from '@app/libs/format'
import useRemember from '@app/libs/useRemember'
import { Inertia } from '@inertiajs/inertia'
import { InertiaLink } from '@inertiajs/inertia-react'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import LockIcon from '@mui/icons-material/Lock'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import Chip from '@mui/material/Chip'
import Collapse from '@mui/material/Collapse'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

const CustomIconButton = styled(IconButton)((props) => {
  const { theme } = props
  if (props['aria-expanded']) {
    return {
      transform: 'rotate(0deg)',
      marginLeft: 'auto',
      transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest,
      }),
    }
  }

  return {
    transform: 'rotate(180deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  }
})

const CustomTypography = styled(Typography)({
  wordBreak: 'break-word',
  textAlign: 'justify',
})

const VoucherDetail = ({ voucher, setOpenGenerate }) => {
  const { t } = useTranslation()

  const [allExpandedVoucher, setAllExpandedVoucher] = useRemember(`VoucherDetail:${voucher.id}`, {
    description: true,
    guide: true,
    condition: true,
    term: true,
  })

  const [anchorEl, setAnchorEl] = useState(null)

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }
  const handleExpand = (attribute) => {
    setAllExpandedVoucher({ ...allExpandedVoucher, [attribute]: !allExpandedVoucher[attribute] })
  }

  const [openModalConfirm, setOpenModalConfirm] = useState(false)
  const handleCloseModal = () => setOpenModalConfirm(false)

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Section>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <ImageSlider images={voucher.images} />
            </Grid>
            <Grid item xs={8}>
              <Grid container spacing={2}>
                <Grid item xs={11}>
                  <Typography variant="h4">{voucher.name}</Typography>
                </Grid>

                <Grid item justifyContent="flex-end" xs={1}>
                  <OptionMenu
                    anchorEl={anchorEl}
                    handleClick={handleClick}
                    handleClose={handleClose}
                    open={null !== anchorEl}
                  >
                    <MenuItem
                      component={InertiaLink}
                      href={route('admin.vouchers.edit', voucher.id)}
                      icon={EditIcon}
                      name={t('BUTTON.EDIT')}
                    />

                    <MenuItem
                      icon={VOUCHER_STATUS.ACTIVE === voucher.status ? LockIcon : LockOpenIcon}
                      name={VOUCHER_STATUS.ACTIVE === voucher.status ? t('VOUCHER.INACTIVE') : t('VOUCHER.ACTIVE')}
                      onClick={() => {
                        handleClose()
                        setOpenModalConfirm(true)
                      }}
                    />

                    <MenuItem
                      icon={AddIcon}
                      name={t('VOUCHER.GENERATE_CODE')}
                      onClick={() => {
                        handleClose()
                        setOpenGenerate(true)
                      }}
                    />
                  </OptionMenu>
                </Grid>
                <ModalUpdateStatusCampaign
                  content={
                    VOUCHER_STATUS.ACTIVE === voucher.status
                      ? t('MODAL.DEACTIVE_CONTENT_VOUCHER')
                      : t('MODAL.ACTIVE_CONTENT_VOUCHER')
                  }
                  handleCloseModal={handleCloseModal}
                  handleSubmit={() => {
                    Inertia.post(route('admin.vouchers.status.change', { voucher: voucher.id, status: voucher.status }))
                    handleCloseModal()
                  }}
                  openModalConfirm={openModalConfirm}
                  title={
                    VOUCHER_STATUS.ACTIVE === voucher.status ? t('MODAL.DEACTIVE_VOUCHER') : t('MODAL.ACTIVE_VOUCHER')
                  }
                />

                <Grid item xs={3}>
                  <Typography color="textSecondary" variant="body1">
                    {t('VOUCHER.MERCHANT_NAME')}
                  </Typography>
                </Grid>

                <Grid item xs={9}>
                  <Typography variant="body1">{voucher.merchant.name}</Typography>
                </Grid>

                <Grid item xs={3}>
                  <Typography color="textSecondary" variant="body1">
                    {t('VOUCHER.TYPE_VOUCHER')}
                  </Typography>
                </Grid>

                <Grid item xs={9}>
                  <CustomChip color={voucher.type} label={t(`VOUCHER.${voucher.type.toUpperCase()}`)} />
                </Grid>

                <Grid item xs={3}>
                  <Typography color="textSecondary" variant="body1">
                    {t('VOUCHER.PRICE')}
                  </Typography>
                </Grid>

                <Grid item xs={9}>
                  <Typography variant="body1">{formatPrice(voucher.price)}</Typography>
                </Grid>

                <Grid item xs={3}>
                  <Typography color="textSecondary" variant="body1">
                    {t('VOUCHER.QUANTITY')}
                  </Typography>
                </Grid>

                <Grid item xs={9}>
                  <Typography variant="body1">{formatNumber(voucher.quantity)}</Typography>
                </Grid>

                {VOUCHER_TYPE.PREMIUM === voucher.type && (
                  <>
                    <Grid item xs={3}>
                      <Typography color="textSecondary" variant="body1">
                        {t('VOUCHER.MIN')}
                      </Typography>
                    </Grid>

                    <Grid item xs={9}>
                      <Typography variant="body1">{voucher.limit_min}</Typography>
                    </Grid>

                    <Grid item xs={3}>
                      <Typography color="textSecondary" variant="body1">
                        {t('VOUCHER.MAX')}
                      </Typography>
                    </Grid>

                    <Grid item xs={9}>
                      <Typography variant="body1">{voucher.limit_max}</Typography>
                    </Grid>
                  </>
                )}

                <Grid item xs={3}>
                  <Typography color="textSecondary" variant="body1">
                    {t('VOUCHER.CATEGORIES')}
                  </Typography>
                </Grid>

                <Grid item xs={9}>
                  {voucher.categories.map((item) => (
                    <Chip key={item.id} label={item.name} sx={{ margin: '2px' }} />
                  ))}
                </Grid>

                <Grid item xs={3}>
                  <Typography color="textSecondary" variant="body1">
                    {t('VOUCHER.EXPIRED_DATE')}
                  </Typography>
                </Grid>

                <Grid item xs={9}>
                  <Typography variant="body1">{toDateTime(voucher.expired_date)}</Typography>
                </Grid>

                <Grid item xs={3}>
                  <Typography color="textSecondary" variant="body1">
                    {t('VOUCHER.REMAIN_TIME')}
                  </Typography>
                </Grid>

                <Grid item xs={9}>
                  {voucher.expired_date ? (
                    toRemainingTime(new Date(), voucher.expired_date)
                  ) : (
                    <Typography variant="body1">{t('DATA.NO_DATA')}</Typography>
                  )}
                </Grid>

                <Grid item xs={3}>
                  <Typography color="textSecondary" variant="body1">
                    {t('VOUCHER.STATUS')}
                  </Typography>
                </Grid>

                <Grid item xs={9}>
                  <CustomChip color={voucher.status} label={t(`VOUCHER.${voucher.status.toUpperCase()}`)} />
                </Grid>

                <Grid item xs={3}>
                  <Typography color="textSecondary" variant="body1">
                    {t('VOUCHER.HASHTAGS')}
                  </Typography>
                </Grid>

                <Grid item xs={9}>
                  {voucher.tags.length ? (
                    voucher.tags.map((item) => <Chip key={item.id} label={item.name} style={{ margin: '2px' }} />)
                  ) : (
                    <Typography variant="body1">{t('DATA.NO_DATA')}</Typography>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Section>
      </Grid>

      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item lg={3} md={6} sm={12} xs={12}>
            <CardWidget
              color="warning"
              primary={voucher.codes_get}
              primaryUnit={t('VOUCHER.CODES')}
              secondary={voucher.total_codes_get_price}
              title={t('VOUCHER.TOTAL_GET_CODES')}
            ></CardWidget>
          </Grid>
          <Grid item lg={3} md={6} sm={12} xs={12}>
            <CardWidget
              color="success"
              primary={voucher.codes_redeem}
              primaryUnit={t('VOUCHER.CODES')}
              secondary={voucher.total_codes_redeem_price}
              title={t('VOUCHER.REDEEMED_CODES')}
            ></CardWidget>
          </Grid>
          <Grid item lg={3} md={6} sm={12} xs={12}>
            <CardWidget
              color="danger"
              primary={voucher.codes_used}
              primaryUnit={t('VOUCHER.CODES')}
              secondary={voucher.total_codes_used_price}
              title={t('VOUCHER.USED_CODES')}
            ></CardWidget>
          </Grid>
          <Grid item lg={3} md={6} sm={12} xs={12}>
            <CardWidget
              primary={voucher.quantity - voucher.codes_get}
              primaryUnit={t('VOUCHER.CODES')}
              secondary={voucher.availablePoints}
              title={t('VOUCHER.REMAIN')}
            ></CardWidget>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Section>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h5">
                {t('VOUCHER.DESCRIPTION')}
                <CustomIconButton
                  aria-expanded={allExpandedVoucher.description}
                  aria-label="show more"
                  onClick={() => handleExpand('description')}
                >
                  <ExpandMoreIcon />
                </CustomIconButton>
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Collapse unmountOnExit in={allExpandedVoucher.description} timeout="auto">
                {voucher.description ? (
                  <CustomTypography
                    dangerouslySetInnerHTML={{ __html: voucher.description }}
                    variant="body1"
                  ></CustomTypography>
                ) : (
                  <Typography variant="body1">{t('DATA.NO_DATA')}</Typography>
                )}
              </Collapse>
            </Grid>
          </Grid>
        </Section>
      </Grid>

      <Grid item xs={12}>
        <Section>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h5">
                {t('VOUCHER.HOW_TO_USE')}{' '}
                <CustomIconButton
                  aria-expanded={allExpandedVoucher.guide}
                  aria-label="show more"
                  onClick={() => handleExpand('guide')}
                >
                  <ExpandMoreIcon />
                </CustomIconButton>
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Collapse unmountOnExit in={allExpandedVoucher.guide} timeout="auto">
                {voucher.how_to_use ? (
                  <CustomTypography
                    dangerouslySetInnerHTML={{ __html: voucher.how_to_use }}
                    variant="body1"
                  ></CustomTypography>
                ) : (
                  <Typography variant="body1">{t('DATA.NO_DATA')}</Typography>
                )}
              </Collapse>
            </Grid>
          </Grid>
        </Section>
      </Grid>

      <Grid item xs={12}>
        <Section>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h5">
                {t('VOUCHER.TERM_AND_CONDITION')}{' '}
                <CustomIconButton
                  aria-expanded={allExpandedVoucher.condition}
                  aria-label="show more"
                  onClick={() => handleExpand('condition')}
                >
                  <ExpandMoreIcon />
                </CustomIconButton>
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Collapse unmountOnExit in={allExpandedVoucher.condition} timeout="auto">
                {voucher.condition ? (
                  <CustomTypography
                    dangerouslySetInnerHTML={{ __html: voucher.condition }}
                    variant="body1"
                  ></CustomTypography>
                ) : (
                  <Typography variant="body1">{t('DATA.NO_DATA')}</Typography>
                )}
              </Collapse>
            </Grid>
          </Grid>
        </Section>
      </Grid>
    </Grid>
  )
}

export default VoucherDetail
